import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { ApiClient } from '../api/client';

type Settings = {
  policy: { enabled: boolean; max_requests: number; max_tokens: number; max_cost: number };
  ready: boolean; daily_global_cost_cap: number; max_active_turns: number;
};
const endpoint = '/api/settings/personal-assistant';

export default function PersonalAgentSettingsPage({ apiClient }: { apiClient: ApiClient }) {
  const [settings, setSettings] = useState<Settings | null>(null);
  const [busy, setBusy] = useState(false);
  const [uncertain, setUncertain] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const refresh = useCallback(async () => {
    setSettings(await apiClient.get<Settings>(endpoint)); setUncertain(false);
  }, [apiClient]);
  useEffect(() => {
    const controller = new AbortController();
    apiClient.get<Settings>(endpoint, { signal: controller.signal }).then(value => {
      if (!controller.signal.aborted) setSettings(value);
    }).catch(() => { if (!controller.signal.aborted) setError('Personal agent settings could not be loaded. Sign in as an administrator and check again.'); });
    return () => controller.abort();
  }, [apiClient]);
  const check = async () => {
    setBusy(true); setError(null);
    try { await refresh(); } catch { setError('Saved settings could not be checked.'); }
    finally { setBusy(false); }
  };
  const toggle = async () => {
    if (!settings || busy || uncertain) return;
    setBusy(true); setError(null);
    try {
      await apiClient.post(endpoint, { enabled: !settings.policy.enabled });
      await refresh();
    } catch {
      setUncertain(true);
      setError('The change was not confirmed. Check saved settings before trying again.');
    } finally { setBusy(false); }
  };
  return <main className="page">
    <p><Link to="/settings">Settings</Link> / Personal agent</p>
    <section className="card"><h1>Personal agent setup</h1>
      <p>Each user has one personal assistant. Applications and conversations are connected when a task needs access.</p>
      <p>The platform supplies the runtime automatically. Users can start without choosing an application, image, executor, or disk size.</p>
      <p>Only authorized external tools are available. The assistant has no local shell, file tools, browser, or plugins.</p>
      {error && <p role="alert">{error}</p>}
      <p role="status">{!settings ? 'Checking settings…' : settings.policy.enabled
        ? 'Personal assistants are enabled.' : 'Personal assistants are disabled.'}</p>
      {settings && <>
        <h2>Default limits</h2>
        <p>The default worker allowance is up to {settings.policy.max_requests} model requests, {settings.policy.max_tokens} tokens, and {settings.policy.max_cost} cost units per UTC day. Start approves the daily allowance; it is spent only on active requests.</p>
        <p>A hosted Gateway can have a separately approved token allowance for its owner. Updated limits apply to new daily allowances; an already issued allowance keeps its original limits and spending until it expires.</p>
        <p>The platform allows {settings.max_active_turns} active assistant turns and reserves at most {settings.daily_global_cost_cap} cost units in daily allowances across users.</p>
        <p>Idle hosted assistants keep their history without a dedicated VM or workspace. Conversation capacity remains separate from the bounded assistant worker pool.</p>
        {!settings.ready && <p>The default worker and private model connection need server configuration before assistants can be enabled.</p>}
        <button type="button" disabled={busy || uncertain || (!settings.policy.enabled && !settings.ready)} onClick={() => { void toggle(); }}>
          {settings.policy.enabled ? 'Disable personal assistants' : 'Enable personal assistants'}
        </button>
      </>}
      <button type="button" disabled={busy} onClick={() => { void check(); }}>Check saved settings</button>
      <p><Link to="/personal-agent">Open Personal agent</Link></p>
      <details><summary>Optional VM runtime</summary><p>The hosted assistant works without a VM. VM execution requires separate operator configuration and approval; existing VM history is retained separately.</p>
        <Link to="/settings/personal-agent/legacy">Legacy runtime administration</Link></details>
    </section>
  </main>;
}
